import {
  cn,
  Flex,
  LabelField,
  LabelFieldError,
  LabelFieldMain,
  LabelFieldSupporting,
} from '@bridebook/aura';
import { ElementRef, forwardRef, PropsWithChildren, ReactNode, useId, useMemo } from 'react';
import { Checkbox, CheckboxProps } from '../checkbox/checkbox';

export type CheckboxFieldProps = Omit<CheckboxProps, 'error'> & {
  label: ReactNode;
  description?: ReactNode;
  error?: boolean | string;
  classNames?: {
    wrapper?: string;
    labelWrapper?: string;
    labelMain?: string;
    labelSupporting?: string;
    labelError?: string;
  };
};

export const CheckboxField = forwardRef<ElementRef<typeof Checkbox>, CheckboxFieldProps>(
  (
    {
      label,
      description,
      size = 'standard',
      id: idFromProps,
      disabled = false,
      error = false,
      classNames,
      ...props
    },
    ref,
  ) => {
    const defaultId = useId();
    const id = idFromProps ?? defaultId;

    const fieldId = `${id}-field`;
    const errorId = `${id}-error`;
    const supportingId = `${id}-supporting`;

    const hasErrorMessage = typeof error === 'string';

    const ariaDescribedby = useMemo(
      () =>
        [description ? supportingId : undefined, hasErrorMessage ? errorId : undefined]
          .filter(Boolean)
          .join(' ') || undefined,
      [description, supportingId, hasErrorMessage, errorId],
    );

    return (
      <Flex className={cn('gap-2', classNames?.wrapper)}>
        <SpacingWrapper size={size}>
          <Checkbox
            size={size}
            error={!!error}
            disabled={disabled}
            id={fieldId}
            ref={ref}
            aria-describedby={ariaDescribedby}
            {...props}
          />
        </SpacingWrapper>
        <LabelField
          size={size}
          disabled={disabled}
          fieldId={fieldId}
          supportingId={supportingId}
          errorId={errorId}
          className={classNames?.labelWrapper}>
          <LabelFieldMain className={classNames?.labelMain}>{label}</LabelFieldMain>
          {description && (
            <LabelFieldSupporting className={classNames?.labelSupporting}>
              {description}
            </LabelFieldSupporting>
          )}
          {hasErrorMessage && (
            <LabelFieldError className={classNames?.labelError}>{error}</LabelFieldError>
          )}
        </LabelField>
      </Flex>
    );
  },
);
CheckboxField.displayName = 'CheckboxField';

// Wrapper used for small checkbox.
// Needed to align the checkbox with the label,
// since the labels title line height is 20px.
const SpacingWrapper = ({ children, size }: PropsWithChildren<{ size: CheckboxProps['size'] }>) => {
  if (size === 'sm') {
    return <div className="flex size-5 items-center justify-center">{children}</div>;
  }
  return <>{children}</>;
};
